import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const ACTION_LABELS = {
  "user.create": { label: "Created account", color: "bg-[#1E3A8A] text-[#F4C430]" },
  "user.approve": { label: "Approved", color: "bg-[#1E3A8A] text-[#F4C430]" },
  "user.reject": { label: "Rejected", color: "bg-[#D32F2F] text-white" },
  "user.suspend": { label: "Suspended", color: "bg-[#D32F2F] text-white" },
  "user.unsuspend": { label: "Reinstated", color: "bg-[#1E3A8A] text-[#F4C430]" },
  "user.remove": { label: "Removed", color: "bg-[#D32F2F] text-white" },
  "user.restore": { label: "Restored", color: "bg-[#1E3A8A] text-[#F4C430]" },
  "user.role_change": { label: "Role changed", color: "bg-[#F4C430] text-[#1E3A8A]" },
};

const ACTION_FILTERS = [
  { key: "all", label: "All actions" },
  { key: "user.create", label: "Created" },
  { key: "user.approve", label: "Approved" },
  { key: "user.reject", label: "Rejected" },
  { key: "user.suspend", label: "Suspended" },
  { key: "user.remove", label: "Removed" },
  { key: "user.restore", label: "Restored" },
];

const formatTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (filter !== "all") params.action = filter;
        const { data } = await api.get("/admin/audit-log", { params });
        setItems(data.items || []);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load audit log");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filter]);

  return (
    <div data-testid="admin-audit">
      <p className="label-eyebrow text-[#1E3A8A]">— Accountability</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Audit log
      </h1>
      <p className="text-[#555] mt-3 max-w-xl">
        Every admin action on user accounts is recorded here — who did what, to whom, and when.
      </p>

      <div className="mt-8 max-w-xs">
        <label className="label-eyebrow text-[#555]">Filter by action</label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            data-testid="audit-filter"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {ACTION_FILTERS.map((a) => (
              <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-8 border border-[#E5E0D8] bg-white">
        {loading && (
          <p className="p-8 label-eyebrow text-[#555]">Loading…</p>
        )}
        {!loading && items.length === 0 && (
          <p className="p-8 label-eyebrow text-[#555]">No admin actions recorded yet.</p>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-[#F4EFE6]/40">
              <tr className="text-left">
                <th className="p-3 label-eyebrow text-[#555]">When</th>
                <th className="p-3 label-eyebrow text-[#555]">Action</th>
                <th className="p-3 label-eyebrow text-[#555]">By</th>
                <th className="p-3 label-eyebrow text-[#555]">Target</th>
                <th className="p-3 label-eyebrow text-[#555]">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const action = ACTION_LABELS[row.action] || {
                  label: row.action,
                  color: "bg-[#E5E0D8] text-[#555]",
                };
                let note = "";
                if (row.action === "user.create" && row.metadata?.role) {
                  note = `As ${row.metadata.role}${row.metadata.email_sent ? " · invite sent" : ""}`;
                } else if (row.action === "user.role_change" && row.metadata?.new_role) {
                  note = `→ ${row.metadata.new_role}`;
                }
                return (
                  <tr
                    key={row.id}
                    data-testid={`audit-row-${row.id}`}
                    className="border-t border-[#E5E0D8]"
                  >
                    <td className="p-3 font-mono-aam text-xs text-[#555] whitespace-nowrap">
                      {formatTime(row.created_at)}
                    </td>
                    <td className="p-3">
                      <span className={`label-eyebrow px-2 py-1 ${action.color}`}>
                        {action.label}
                      </span>
                    </td>
                    <td className="p-3">
                      <p className="font-serif-display text-base text-[#111]">{row.actor_name}</p>
                      <p className="font-mono-aam text-xs text-[#555] mt-0.5">{row.actor_email}</p>
                    </td>
                    <td className="p-3">
                      {row.target_name ? (
                        <>
                          <p className="font-serif-display text-base text-[#111]">{row.target_name}</p>
                          <p className="font-mono-aam text-xs text-[#555] mt-0.5">{row.target_email}</p>
                        </>
                      ) : (
                        <span className="text-[#888]">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-[#555]">{note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-[#555]">
        Showing {items.length} entries. Logs are append-only and cannot be edited.
      </p>
    </div>
  );
}
